if (!customElements.get('featured-collection')) {
  customElements.define(
    'featured-collection',
    class FeaturedCollection extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.mountLatestArticles();
        this.mountRandomLibraryArticles();

        this.selectors = {
          sliderWrapper: '.featured-collection__items',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          pagination: '.swiper-pagination',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.blockId = this.dataset.blockId;
        this.block = this.closest(`[data-id="${this.blockId}"]`);
        this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items);
        this.tabletItems = parseInt(this.dataset.tabletItems);
        this.laptopItems = parseInt(this.dataset.laptopItems);
        this.paginationType = this.dataset.paginationType || 'bullets';

        this.sliderInstance = false;

        if (!this.enableSlider) return;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);
        this.init();
      }

      mountLatestArticles() {
        const section = this.closest('.featured-collection');
        if (!section || section.dataset.latestArticlesLoaded === 'true') return;

        const heading = section.querySelector('.section__header .rich-text__heading');
        const headingText = heading ? heading.textContent.trim().toLowerCase() : '';
        if (headingText !== 'nyheter') return;

        section.dataset.latestArticlesLoaded = 'true';

        const url = new URL('/blogs/skadedyrguiden', window.location.origin);
        url.searchParams.set('view', 'home-latest-articles');

        const currentParams = new URLSearchParams(window.location.search);
        const previewThemeId = currentParams.get('preview_theme_id');
        const previewBar = currentParams.get('pb');
        if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
        if (previewBar) url.searchParams.set('pb', previewBar);

        fetch(url.toString(), { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error(`Could not load latest articles: ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const articleBlock = parsed.querySelector('[data-latest-articles-home]');
            if (!articleBlock) throw new Error('Latest articles block was not found in response');
            section.insertAdjacentHTML('afterend', articleBlock.outerHTML);
          })
          .catch((error) => {
            delete section.dataset.latestArticlesLoaded;
            console.error(error);
          });
      }

      mountRandomLibraryArticles() {
        if (!document.body.classList.contains('index-template')) return;
        if (document.querySelector('[data-random-library-articles-mounted]')) return;
        if (document.documentElement.dataset.randomLibraryArticlesLoading === 'true') return;

        const main = document.getElementById('MainContent');
        if (!main) return;

        document.documentElement.dataset.randomLibraryArticlesLoading = 'true';

        const pageSize = 50;
        const currentParams = new URLSearchParams(window.location.search);
        const previewThemeId = currentParams.get('preview_theme_id');
        const previewBar = currentParams.get('pb');

        const buildUrl = (page) => {
          const url = new URL('/blogs/skadedyrbiblioteket', window.location.origin);
          url.searchParams.set('view', 'home-random-library');
          url.searchParams.set('page', String(page));
          url.searchParams.set('_random', `${Date.now()}-${page}-${Math.random()}`);
          if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
          if (previewBar) url.searchParams.set('pb', previewBar);
          return url;
        };

        const fetchPage = (page) =>
          fetch(buildUrl(page).toString(), { credentials: 'same-origin', cache: 'no-store' })
            .then((response) => {
              if (!response.ok) throw new Error(`Could not load Skadedyrbiblioteket page ${page}: ${response.status}`);
              return response.text();
            })
            .then((html) => {
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              const block = parsed.querySelector('[data-random-library-articles-home]');
              if (!block) throw new Error(`Skadedyrbiblioteket article block was not found for page ${page}`);
              return block;
            });

        fetchPage(1)
          .then((baseBlock) => {
            const articleCount = parseInt(baseBlock.dataset.articleCount, 10);
            if (!Number.isFinite(articleCount) || articleCount < 1) {
              throw new Error('Skadedyrbiblioteket has no available articles');
            }

            const selectionCount = Math.min(4, articleCount);
            const selectedIndexes = new Set();
            while (selectedIndexes.size < selectionCount) {
              selectedIndexes.add(Math.floor(Math.random() * articleCount));
            }

            const orderedIndexes = Array.from(selectedIndexes);
            const neededPages = new Set(orderedIndexes.map((index) => Math.floor(index / pageSize) + 1));
            const pageBlocks = new Map([[1, baseBlock]]);

            return Promise.all(
              Array.from(neededPages)
                .filter((page) => page !== 1)
                .map((page) =>
                  fetchPage(page).then((block) => {
                    pageBlocks.set(page, block);
                  })
                )
            ).then(() => ({ baseBlock, orderedIndexes, pageBlocks }));
          })
          .then(({ baseBlock, orderedIndexes, pageBlocks }) => {
            const grid = baseBlock.querySelector('[data-random-library-grid]');
            if (!grid) throw new Error('Skadedyrbiblioteket grid was not found');

            const selectedCards = orderedIndexes
              .map((globalIndex) => {
                const page = Math.floor(globalIndex / pageSize) + 1;
                const localIndex = globalIndex % pageSize;
                const pageBlock = pageBlocks.get(page);
                if (!pageBlock) return null;
                const cards = pageBlock.querySelectorAll('[data-random-library-card]');
                return cards[localIndex] || null;
              })
              .filter(Boolean);

            if (!selectedCards.length) throw new Error('No Skadedyrbiblioteket cards could be selected');

            grid.replaceChildren(...selectedCards.map((card) => document.importNode(card, true)));
            baseBlock.setAttribute('data-random-library-articles-mounted', 'true');
            main.insertAdjacentElement('afterend', baseBlock);
            delete document.documentElement.dataset.randomLibraryArticlesLoading;
          })
          .catch((error) => {
            delete document.documentElement.dataset.randomLibraryArticlesLoading;
            console.error(error);
          });
      }

      init() {
        if (FoxTheme.config.mqlMobile) {
          this.destroySlider();
        } else {
          this.initSlider();
        }
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;
        const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--f-column-gap');
        const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10;

        this.sliderOptions = {
          slidesPerView: 2,
          spaceBetween: spaceBetween,
          navigation: {
            nextEl: this.block.querySelector(this.selectors.nextEl),
            prevEl: this.block.querySelector(this.selectors.prevEl),
          },
          pagination: {
            el: this.block.querySelector(this.selectors.pagination),
            type: this.paginationType,
          },
          breakpoints: {
            768: {
              slidesPerView: this.tabletItems,
            },
            1024: {
              slidesPerView: this.laptopItems,
            },
            1280: {
              slidesPerView: this.items,
            },
          },
          loop: false,
          threshold: 2,
          watchSlidesProgress: true,
          mousewheel: {
            enabled: true,
            forceToAxis: true,
          },
        };

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, [FoxTheme.Swiper.Mousewheel]);
        this.sliderInstance.init();

        this.calcNavButtonsPosition();
        this.fixQuickviewDuplicate();
      }

      destroySlider() {
        this.classList.remove(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
        this.sliderWrapper.classList.add(this.classes.grid);
        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      fixQuickviewDuplicate() {
        let modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }

      calcNavButtonsPosition() {
        if (this.dataset.navigationPosition !== 'center_image') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        const controls = this.block.querySelector('.swiper-controls');
        if (controls && firstMedia && firstMedia.clientHeight > 0) {
          controls.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
        }
      }
    }
  );
}
